import { FC, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { clearBebyggelseobjekt, getBebyggelseobjekt } from 'store/bebyggelseobjektSlice';
import { setMapMode } from 'store/mapSlice';
import { RootState } from 'store/rootReducer';
import { clearVinjett, fetchArkiveradResurs } from 'store/vinjettSlice';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';
import { useDrawerMobileTemplate } from 'hooks/useDrawerMobileTemplate';
import { useInitialFocusOnExtent } from 'hooks/useInitialFocusOnExtent';

import { RaaSpinner } from 'raa-react';

import BebyggelseSkyddstypSection from 'components/bebyggelseobjekt/sections/BebyggelseSkyddstypSection';
import BebyggelseTitleSection from 'components/bebyggelseobjekt/sections/BebyggelseTitleSection';
import RegistreringshistorikSection from 'components/bebyggelseobjekt/sections/RegistreringshistorikSection';
import {
  DrawerBody,
  DrawerContainer,
  DrawerSection,
  NavigationDrawerHead
} from 'components/drawerSections/DrawerSections';
import { replaceBebyggelseobjektFeaturesInActiveSource } from 'components/map/Layers';

import {
  BebyggelseobjektPublicerad,
  BebyggelseobjektUtkast,
  BebyggelseobjektVersionHistorisk,
  Skyddstyp,
  Socken
} from 'models';

import { BebyggelseklassEnum, BebyggelseobjektgruppEnum, MapModeEnum } from 'enums';

import BebyggelseBeskrivningSection from './sections/BebyggelseBeskrivningSection';
import BebyggelseFastigheterSection from './sections/BebyggelseFastigheterSection';
import BebyggelseHistorikSection from './sections/BebyggelseHistorikSection';
import SammansattBebyggelseIngaendeObjektSection from './sections/SammansattBebyggelseIngaendeObjektSection';
import BebyggelseSkyddbestammelserSection from './sections/SammansattBebyggelseSkyddbestammelserSection';
import BebyggelseDokumentationSection from './sections/dokumentation/BebyggelseDokumentationSection';

import { getIconBySkydd, isSkyddHavt } from 'utilities/bebyggelseobjektUtilities';
import { getIngaendeAsPublicerade } from 'utilities/getIngaendeAsPublicerade';
import { renderWithParagraphs } from 'utilities/renderWithParagraphs';

export const isOmrade = (
  item: BebyggelseobjektUtkast | BebyggelseobjektPublicerad | BebyggelseobjektVersionHistorisk
) =>
  item?.bebyggelseklass === BebyggelseklassEnum.OMRADE ||
  ('bebyggelseobjektgrupp' in item && item.bebyggelseobjektgrupp === BebyggelseobjektgruppEnum.OMRADE);

export const isSammansatt = (
  item: BebyggelseobjektUtkast | BebyggelseobjektPublicerad | BebyggelseobjektVersionHistorisk
) =>
  item?.bebyggelseklass === BebyggelseklassEnum.SAMMANSATT ||
  ('bebyggelseobjektgrupp' in item && item.bebyggelseobjektgrupp === BebyggelseobjektgruppEnum.SAMMANSATT);

export const isEnskiltBebyggelseobjekt = (
  item: BebyggelseobjektUtkast | BebyggelseobjektPublicerad | BebyggelseobjektVersionHistorisk
) =>
  item?.bebyggelseklass === BebyggelseklassEnum.BYGGNAD ||
  item?.bebyggelseklass === BebyggelseklassEnum.ANLAGGNING ||
  item?.bebyggelseklass === BebyggelseklassEnum.PLATS ||
  ('bebyggelseobjektgrupp' in item &&
    item.bebyggelseobjektgrupp === BebyggelseobjektgruppEnum.ENSKILT_BEBYGGELSEOBJEKT);

const BebyggelseobjektPresentation: FC = () => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { requestStatus, data: bebyggelseobjektPublicerad } = useAppSelector(
    (state: RootState) => state.bebyggelseobjekt
  );
  const domaner = useAppSelector((state: RootState) => state.bebyggelseobjektDomaner.data);
  const { data: vinjettData, requestStatus: vinjettStatus } = useAppSelector((state: RootState) => state.vinjett);

  const { setDrawerMobileTemplate } = useDrawerMobileTemplate();

  useEffect(() => {
    if (id) {
      dispatch(getBebyggelseobjekt(id));
    }

    //Cleanup
    return () => {
      dispatch(clearBebyggelseobjekt());
      replaceBebyggelseobjektFeaturesInActiveSource([]);
      dispatch(setMapMode(MapModeEnum.DEFAULT));
      setDrawerMobileTemplate(undefined);
    };
  }, [id, dispatch, setDrawerMobileTemplate]);

  useEffect(() => {
    if (bebyggelseobjektPublicerad) {
      setDrawerMobileTemplate(
        <div className="tw-flex tw-flex-col tw-gap-2 tw-p-4">
          <h2 className="tw-heading-md tw-w-64 tw-truncate md:tw-w-full">{bebyggelseobjektPublicerad.titel}</h2>
          <div className="tw-flex tw-items-center tw-justify-start">
            <img
              className="tw-mr-2"
              src={`data:image/svg+xml;utf8,${encodeURIComponent(
                getIconBySkydd(bebyggelseobjektPublicerad.skydd.skyddstyp, isSkyddHavt(bebyggelseobjektPublicerad))
              )}`}
              alt="Ikon för bebyggelseobjekt"
            />
            <p>
              {bebyggelseobjektPublicerad.skydd.skyddstyp_namn}
              {bebyggelseobjektPublicerad.sammansatt?.havt_skydd && ' (HÄVT)'}
            </p>
          </div>
        </div>
      );
    }
  }, [setDrawerMobileTemplate, bebyggelseobjektPublicerad]);

  useInitialFocusOnExtent(
    useCallback(
      () =>
        bebyggelseobjektPublicerad &&
        replaceBebyggelseobjektFeaturesInActiveSource([
          bebyggelseobjektPublicerad,
          ...getIngaendeAsPublicerade(bebyggelseobjektPublicerad)
        ]),
      [bebyggelseobjektPublicerad]
    )
  );

  useEffect(() => {
    if (bebyggelseobjektPublicerad?.vinjett?.typ != null) {
      dispatch(fetchArkiveradResurs(bebyggelseobjektPublicerad.vinjett.url));
    } else {
      dispatch(clearVinjett());
    }
  }, [dispatch, bebyggelseobjektPublicerad?.vinjett]);

  if (requestStatus === 'pending') {
    return (
      <div className="tw-flex tw-h-full tw-items-center tw-justify-center">
        <RaaSpinner />
      </div>
    );
  } else if (!bebyggelseobjektPublicerad) {
    return <></>;
  }

  return (
    <DrawerContainer>
      <NavigationDrawerHead />
      <DrawerBody>
        <BebyggelseTitleSection
          skyddstyp={bebyggelseobjektPublicerad.skydd.skyddstyp}
          skyddstypNamn={bebyggelseobjektPublicerad.skydd.skyddstyp_namn}
          data={bebyggelseobjektPublicerad}
          vinjett={vinjettData}
          vinjettRequestStatus={vinjettStatus}
          socken={bebyggelseobjektPublicerad.geografisk_indelning.socken
            .map((socken: Socken) => socken.socken_namn)
            .join(', ')}
          domaner={domaner}
        />
        <BebyggelseSkyddstypSection
          motivering={bebyggelseobjektPublicerad.skydd.motivering}
          karaktarsdrag={bebyggelseobjektPublicerad.karaktarsdrag}
          skyddstyp={bebyggelseobjektPublicerad.skydd.skyddstyp}
          lokal_klassificering={bebyggelseobjektPublicerad.lokal_klassificering}
          ansvarig_myndighet={
            domaner?.skyddstyper.find(
              (skyddsTyp: Skyddstyp) => skyddsTyp.skyddstyp === bebyggelseobjektPublicerad.skydd.skyddstyp
            )?.ansvarig_myndighet
          }
          sammansattParentId={bebyggelseobjektPublicerad.sammansatt_parent_id}
          sammansattParentNamn={bebyggelseobjektPublicerad.sammansatt_parent_namn}
          isPublicerad={true}
          skydd_subtyp={bebyggelseobjektPublicerad.skydd.kyrkligtkulturminneskyddstyp_namn}
        />

        {bebyggelseobjektPublicerad.bebyggelseklass === BebyggelseklassEnum.SAMMANSATT &&
          bebyggelseobjektPublicerad.sammansatt && (
            <>
              <BebyggelseSkyddbestammelserSection
                sammansattParentId={bebyggelseobjektPublicerad.sammansatt_parent_id}
                skyddsbestammelser={bebyggelseobjektPublicerad.sammansatt?.skyddsbestammelser}
              />
              <SammansattBebyggelseIngaendeObjektSection
                ingaendeObject={bebyggelseobjektPublicerad.sammansatt.ingaendeobjekt}
                isPublicerad={true}
                havtSkydd={bebyggelseobjektPublicerad.sammansatt.havt_skydd}
                skyddstyp={bebyggelseobjektPublicerad.skydd.skyddstyp}
              />
            </>
          )}

        <BebyggelseBeskrivningSection
          domaner={domaner}
          beskrivning={bebyggelseobjektPublicerad.beskrivning}
          egenskaper={!isOmrade(bebyggelseobjektPublicerad) ? bebyggelseobjektPublicerad.egenskaper : undefined}
        />

        <BebyggelseHistorikSection
          historik={bebyggelseobjektPublicerad.historik || ''}
          handelser={bebyggelseobjektPublicerad.handelser}
          domaner={domaner}
        />

        <BebyggelseDokumentationSection
          uri={bebyggelseobjektPublicerad.uri}
          includeRitningar={bebyggelseobjektPublicerad.bebyggelseklass === BebyggelseklassEnum.SAMMANSATT}
          totalDocuments={bebyggelseobjektPublicerad.arkiv_ref.length}
        />

        {(isOmrade(bebyggelseobjektPublicerad) || isSammansatt(bebyggelseobjektPublicerad)) && (
          <BebyggelseFastigheterSection fastigheter={bebyggelseobjektPublicerad.berorda_fastigheter} />
        )}

        <DrawerSection>
          <h2 className="tw-heading-md tw-mb-4">Referenser</h2>
          {renderWithParagraphs(bebyggelseobjektPublicerad.referenser || 'Inga referenser registrerade')}
        </DrawerSection>
        <RegistreringshistorikSection bebyggelseobjektId={bebyggelseobjektPublicerad.id} />
      </DrawerBody>
    </DrawerContainer>
  );
};

export default BebyggelseobjektPresentation;
